import { FC, useCallback, useContext, useState } from 'react';

import { COUNTRY_CODES } from '@savgroup-front-common/constants';
import { Timeslot } from '@savgroup-front-common/types';

import { Carriers } from '../../api/Claim/getClaimGroupTransportPlanQuery';
import AppointmentCalendar from '../../components/AppointmentCalendar/AppointmentCalendar';
import { useGetHomePickupSchedule } from '../../hooks';
import useGetFileHandling from '../../hooks/useGetFileHandling';
import { BannersProviderContext } from '../../molecules/NotificationsProvider';
import { BaseLoader } from '../BaseLoader';

import ConfirmCancelModal from './ConfirmCancelModal';
import useCreateHomePickupCommand from './hooks/useCreateHomePickup';
import { useNavigateCalendar } from './hooks/useNavigateCalendar';

interface AppointmentCardContentProps {
  fileId: string;
  carrier: Carriers;
  carrierForTMS?: string;
  countryCode: COUNTRY_CODES;
  onPreviousStepClick?: () => void;
  onCancel?: () => void;
  onSubmit?: () => void;
}

export const AppointmentCardContent: FC<AppointmentCardContentProps> = ({
  fileId,
  carrier,
  carrierForTMS,
  countryCode,
  onPreviousStepClick,
  onCancel,
  onSubmit,
}) => {
  const { removeAllNotifications } = useContext(BannersProviderContext);

  const {
    startTime,
    endTime,
    handleNextPage,
    handlePreviousPage,
    isFirstPage,
  } = useNavigateCalendar();

  const { fileHandling } = useGetFileHandling({ fileId });

  const { detailedSchedule: schedules } = useGetHomePickupSchedule({
    carrier,
    carrierForTMS,
    startTimeInLocalRecipientTimezone: startTime,
    endTimeInLocalRecipientTimezone: endTime,
    countryCode,
    postalCode: fileHandling?.deposit?.address.postalCode || '',
    fileId,
  });

  const { handleCreateHomePickup, isLoading: isSubmitLoading } =
    useCreateHomePickupCommand({
      fileId,
      onSuccess: onSubmit,
    });

  const [isConfirmCancelOpen, setIsConfirmCancelOpen] = useState(false);

  const setConfirmModalOpen = (isOpen: boolean) => {
    setIsConfirmCancelOpen(isOpen);
  };

  const handleCloseModal = useCallback(() => setConfirmModalOpen(false), []);

  if (!schedules) {
    return <BaseLoader />;
  }

  return (
    <>
      <AppointmentCalendar
        schedules={schedules}
        isSubmitLoading={isSubmitLoading}
        onValidateSchedule={async ({
          selectedTimeslot,
        }: {
          selectedTimeslot?: Timeslot | undefined;
        }) => {
          if (selectedTimeslot) {
            return await handleCreateHomePickup({
              appointmentTime: {
                allDaySchedule: selectedTimeslot?.allDaySchedule,
                endLocalTime: selectedTimeslot?.end,
                startLocalTime: selectedTimeslot?.start,
                carrier,
                countryCode,
                date: selectedTimeslot?.start,
                internalId: selectedTimeslot?.internalId,
              },
            });
          }

          return {
            failure: true,
            errors: [],
          };
        }}
        startTime={startTime}
        onPageNextClick={() => {
          removeAllNotifications();
          handleNextPage();
        }}
        onPagePreviousClick={() => {
          removeAllNotifications();
          handlePreviousPage();
        }}
        isFirstPage={isFirstPage}
        onPreviousStepClick={onPreviousStepClick}
      />
      {onCancel && (
        <ConfirmCancelModal
          isConfirmCancelOpen={isConfirmCancelOpen}
          onCloseModal={handleCloseModal}
          onSubmit={onCancel}
        />
      )}
    </>
  );
};
